import React, { useMemo, useState } from 'react';
import { TARIFA } from '../data';
import type { AppState, Consumo, Sucursal } from '../types';
import { PageShell, StatCard, T, glassLight, inputStyle, primaryButton, ghostButton, useIsMobile, usePagination, Pagination, Modal } from '../ui';
const money = (n: number) => 'RD$ ' + Math.round(n).toLocaleString('es-DO');
const kwhFmt = (n: number) => Math.round(n).toLocaleString('es-DO') + ' kWh';
const monthLabel = (ts: number) => new Date(ts).toLocaleDateString('es-DO', {
  month: 'short',
  year: '2-digit'
});
// Fecha + hora en horario de República Dominicana (America/Santo_Domingo).
const drDateTime = (ts: number) => new Date(ts).toLocaleString('es-DO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Santo_Domingo'
});
// Consumo de cada lectura = lectura actual − lectura anterior (cronológico).
function buildSeries(consumos: Consumo[]) {
  const asc = [...consumos].sort((a, b) => a.fecha - b.fecha);
  return asc.map((c, i) => ({
    ...c,
    consumo: i === 0 ? 0 : Math.max(0, c.kwh - asc[i - 1].kwh)
  }));
}
interface Props {
  state: AppState;
  sucursal: Sucursal;
  sucursalesDisponibles: Sucursal[];
  puntoFijo: boolean;
  mostrarCosto?: boolean;
  onChangeSucursal: (id: string) => void;
  onSave: (sucursalId: string, fecha: number, kwh: number) => void;
  onDelete?: (id: string) => void;
  canArchive?: boolean;
}
export function ConsumoEnergetico({
  state,
  sucursal,
  sucursalesDisponibles,
  puntoFijo,
  mostrarCosto = true,
  onChangeSucursal,
  onSave,
  onDelete,
  canArchive
}: Props) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const today = new Date();
  const [fechaStr, setFechaStr] = useState(today.toISOString().slice(0, 10));
  const [lectura, setLectura] = useState('');
  const series = useMemo(() => buildSeries(state.consumos.filter(c => c.sucursalId === sucursal.id)), [state.consumos, sucursal.id]);
  const historial = useMemo(() => [...series].reverse().filter(c => tab === 'activos' ? !c.archivado : c.archivado), [series, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems: histItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(historial, 8);
  const ultima = series[series.length - 1];
  const consumoActual = ultima?.consumo ?? 0;
  const now = new Date();
  const inMonth = (ts: number, offset: number) => {
    const d = new Date(ts);
    const ref = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
  };
  const consumoMes = series.filter(c => inMonth(c.fecha, 0)).reduce((a, c) => a + c.consumo, 0);
  const consumoMesAnterior = series.filter(c => inMonth(c.fecha, 1)).reduce((a, c) => a + c.consumo, 0);
  const costoMes = consumoMes * TARIFA;
  const variacion = consumoMesAnterior > 0 ? Math.round((consumoMes - consumoMesAnterior) / consumoMesAnterior * 100) : null;
  // Últimos 6 meses para el gráfico
  const chartData = series.filter(c => c.consumo > 0).slice(-6);
  const maxConsumo = Math.max(1, ...chartData.map(c => c.consumo));
  const ultimaLectura = ultima?.kwh;
  const guardar = () => {
    const kwh = parseFloat(lectura);
    if (Number.isNaN(kwh) || kwh < 0) {
      alert('Ingresa la lectura del medidor en kWh.');
      return;
    }
    // El medidor es acumulativo: una lectura menor que la anterior es inválida
    // y no se permite registrar.
    if (ultimaLectura !== undefined && kwh < ultimaLectura) {
      alert(`La lectura (${kwhFmt(kwh)}) no puede ser menor que la anterior (${kwhFmt(ultimaLectura)}). El medidor es acumulativo, ingresa un valor igual o mayor.`);
      return;
    }
    // Guardamos la fecha elegida con la HORA exacta del registro (zona RD).
    const ahora = new Date();
    const hh = String(ahora.getHours()).padStart(2, '0');
    const mm = String(ahora.getMinutes()).padStart(2, '0');
    const ss = String(ahora.getSeconds()).padStart(2, '0');
    const fecha = new Date(`${fechaStr}T${hh}:${mm}:${ss}`).getTime();
    onSave(sucursal.id, fecha, kwh);
    setLectura('');
  };
  return <PageShell title="Control de Consumo Energético" sub="Registra la lectura de tu medidor. El sistema calcula el resto." data-id="element-329" data-mp="39blrwmefqo3o">
      
      <style data-id="element-330" data-mp="5wptkk5466o4">{`
        .ce-bar { transition: height .4s cubic-bezier(.2,.8,.2,1); }
        .ce-save:hover { opacity:.9; transform: translateY(-1px); }
        .ce-save { transition: all .15s; }
      `}</style>

      {/* ── Tarjeta principal de registro ── */}
      <div style={{
      background: 'linear-gradient(135deg, rgba(255,107,1,.08), rgba(255,107,1,.02))',
      border: `1px solid rgba(255,107,1,.25)`,
      borderRadius: 20,
      padding: isMobile ? 18 : 26
    }} data-id="element-331" data-mp="3t3s55nm49vpp">
        
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 22
      }} data-id="element-332" data-mp="2cnp7f6fvn8n6">
          
          <div data-id="element-333" data-mp="x3ycxm0d18hj">
            <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.accent,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            marginBottom: 6
          }} data-id="element-334" data-mp="x5j5j6346hcr">
              
              Punto Comercial
            </div>
            {puntoFijo ? <div style={{
            fontSize: 22,
            fontWeight: 800,
            color: T.text,
            letterSpacing: '-0.02em'
          }} data-id="element-335" data-mp="1autfkgk9mtco">
              
                {sucursal.nombre}
              </div> : <select value={sucursal.id} onChange={e => onChangeSucursal(e.target.value)} style={{
            ...inputStyle,
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? 0 : 220,
            fontSize: isMobile ? 16 : 18,
            fontWeight: 700,
            padding: '8px 12px'
          }} data-id="element-336" data-mp="1kuk7qblcsw9l">
              
                {sucursalesDisponibles.map(s => <option key={s.id} value={s.id} data-id="element-337" data-mp="xtlgwxhd3nw4">
                    {s.nombre}
                  </option>)}
              </select>}
            <div style={{
            fontSize: 12,
            color: T.textSecondary,
            marginTop: 5
          }} data-id="element-338" data-mp="svjvw70dlei0">
              
              {sucursal.direccion}
            </div>
          </div>
          {ultimaLectura !== undefined && <div style={{
          textAlign: 'right'
        }} data-id="element-339" data-mp="1gquxm6vayn9o">
            
              <div style={{
            fontSize: 11,
            color: T.textMuted,
            fontWeight: 600
          }} data-id="element-340" data-mp="1bsba421293f7">
              
                Última lectura
              </div>
              <div style={{
            fontSize: 16,
            fontWeight: 700,
            color: T.text,
            marginTop: 2
          }} data-id="element-341" data-mp="1li4mh5911mki">
              
                {kwhFmt(ultimaLectura)}
              </div>
            </div>}
        </div>

        <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr auto',
        gap: 14,
        alignItems: 'end',
        maxWidth: 720
      }} data-id="element-342" data-mp="1uk42pvc7wmme">
          
          <div data-id="element-343" data-mp="2j2cy4jl19yhj">
            <label style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            marginBottom: 7
          }} data-id="element-344" data-mp="2j2wevwyy6gtz">
              
              Fecha
            </label>
            <input type="date" value={fechaStr} onChange={e => setFechaStr(e.target.value)} style={inputStyle} data-id="element-345" data-mp="1vca7m7wrp0l1" />
            
          </div>
          <div data-id="element-346" data-mp="1lgp0jvcuda4f">
            <label style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            marginBottom: 7
          }} data-id="element-347" data-mp="1lh8hb8qr9sgv">
              
              Lectura del Medidor (kWh)
            </label>
            <input type="number" min="0" inputMode="decimal" value={lectura} onChange={e => setLectura(e.target.value)} placeholder="Ej: 290500" style={inputStyle} onKeyDown={e => {
            if (e.key === 'Enter') guardar();
          }} data-id="element-348" data-mp="1hg530qocw4hr" />
            
          </div>
          <button className="ce-save" onClick={guardar} style={{
          ...primaryButton,
          padding: '12px 22px',
          fontSize: 14,
          height: 44,
          width: isMobile ? '100%' : 'auto'
        }} data-id="element-349" data-mp="1mwkvgwzalf5s">
            
            Guardar Consumo
          </button>
        </div>
        <div style={{
        fontSize: 12,
        color: T.textMuted,
        marginTop: 12
      }} data-id="element-350" data-mp="1956eswsuxq1w">
          
          Solo escribe el número que muestra el medidor. El sistema calcula el
          consumo y el costo automáticamente.
        </div>
      </div>

      {/* ── Resultados ── */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))',
      gap: 12
    }} data-id="element-351" data-mp="v6ggxey7rmch">
        
        <StatCard label="Consumo actual" value={kwhFmt(consumoActual)} note="Última lectura registrada" accent={T.info} data-id="element-352" data-mp="31v8q4h4am8ml" />
        
        <StatCard label="Consumo del mes" value={kwhFmt(consumoMes)} note={variacion === null ? 'Mes en curso' : `${variacion >= 0 ? '▲' : '▼'} ${Math.abs(variacion)}% vs mes anterior`} accent={variacion !== null && variacion > 0 ? T.warn : T.ok} data-id="element-353" data-mp="36tjxml74871n" />
        
        {mostrarCosto && <StatCard label="Costo estimado" value={money(costoMes)} note={`Tarifa interna ${TARIFA} RD$/kWh`} accent="#c4b5fd" data-id="element-354" data-mp="2j50q47vw3yam" />}
      </div>

      {/* ── Gráfico de consumo mensual ── */}
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: T.textMuted,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      marginTop: 8,
      marginBottom: 4
    }} data-id="element-355" data-mp="2n3zv1shmfv22">
        
        Consumo mensual
      </div>
      <div style={{
      ...glassLight,
      padding: isMobile ? 16 : 24
    }} data-id="element-356" data-mp="1zfqdj2rfxtgq">
        
        {chartData.length === 0 ? <div style={{
        textAlign: 'center',
        color: T.textMuted,
        fontSize: 13,
        padding: '24px 0'
      }} data-id="element-357" data-mp="24e9zh361b593">
          
            Aún no hay suficientes lecturas para graficar.
          </div> : <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: isMobile ? 8 : 18,
        height: isMobile ? 160 : 200,
        padding: '0 4px'
      }} data-id="element-358" data-mp="3ufam1hsoq7rf">
          
            {chartData.map(c => {
          const h = Math.round(c.consumo / maxConsumo * 160) + 4;
          const cost = c.consumo * TARIFA;
          return <div key={c.id} style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }} data-id="element-359" data-mp="3blydjyzsafiz">
                
                  <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: T.text
            }} data-id="element-360" data-mp="3ldkv0d2ptau6">
                  
                    {Math.round(c.consumo / 1000)}k
                  </div>
                  <div className="ce-bar" title={`${kwhFmt(c.consumo)} · ${money(cost)}`} style={{
              width: '100%',
              maxWidth: 54,
              height: h,
              borderRadius: '8px 8px 0 0',
              background: 'linear-gradient(180deg,#FF8C00,#FF5A00)',
              boxShadow: '0 0 18px rgba(255,107,1,.35)'
            }} data-id="element-361" data-mp="2sudzs34b8bpm" />
                
                  <div style={{
              fontSize: 11,
              color: T.textMuted,
              fontWeight: 600
            }} data-id="element-362" data-mp="26ex4n54qz3j6">
                  
                    {monthLabel(c.fecha)}
                  </div>
                </div>;
        })}
          </div>}
      </div>

      {/* ── Comparación ── */}
      <div style={{
      ...glassLight,
      padding: 20,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap'
    }} data-id="element-363" data-mp="1sobhv5c6a9g0">
        
        <div data-id="element-364" data-mp="1iryqwf80i9oy">
          <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: T.text
        }} data-id="element-365" data-mp="1irozcn7bjuh5">
            
            Comparación con el mes anterior
          </div>
          <div style={{
          fontSize: 12,
          color: T.textSecondary,
          marginTop: 3
        }} data-id="element-366" data-mp="1459syhtes4ko">
            
            Mes actual {kwhFmt(consumoMes)} · Mes anterior{' '}
            {kwhFmt(consumoMesAnterior)}
          </div>
        </div>
        <div style={{
        background: variacion !== null && variacion > 0 ? T.warnSoft : T.okSoft,
        color: variacion !== null && variacion > 0 ? T.warn : T.ok,
        borderRadius: 99,
        padding: '8px 16px',
        fontWeight: 700,
        fontSize: 14
      }} data-id="element-367" data-mp="102li64l4wnl2">
          
          {variacion === null ? 'Sin referencia' : `${variacion >= 0 ? '+' : ''}${variacion}%`}
        </div>
      </div>

      {/* ── Historial ── */}
      <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 8,
      marginBottom: 4
    }} data-id="element-368" data-mp="1iy38u4clempv">
        
        <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: T.textMuted,
        letterSpacing: '.1em',
        textTransform: 'uppercase'
      }} data-id="element-369" data-mp="2bia54vye1mlv">
          
          Historial de registros
        </div>
        <div style={{
        display: 'flex',
        gap: 8
      }} data-id="element-370" data-mp="2gs4i6ajuqjh7">
          
          <button onClick={() => {
          setTab('activos');
          setPage(1);
        }} style={{
          ...ghostButton,
          padding: '4px 8px',
          fontSize: 11,
          background: tab === 'activos' ? T.accentSoft : 'transparent',
          color: tab === 'activos' ? T.accent : T.textMuted
        }} data-id="element-371" data-mp="2ungdok5u0dz6">
            
            Activos
          </button>
          {canArchive && <button onClick={() => {
          setTab('archivados');
          setPage(1);
        }} style={{
          ...ghostButton,
          padding: '4px 8px',
          fontSize: 11,
          background: tab === 'archivados' ? T.accentSoft : 'transparent',
          color: tab === 'archivados' ? T.accent : T.textMuted
        }} data-id="element-372" data-mp="2bw5n6440q37t">
            
              Archivados
            </button>}
        </div>
      </div>
      <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-373" data-mp="1wsw67pp5k3r2">
        
        <div style={{
        overflowX: 'auto'
      }} data-id="element-374" data-mp="1wv0ai8qyr4rv">
          
          <div style={{
          display: 'grid',
          gridTemplateColumns: mostrarCosto ? onDelete ? '1.2fr 1fr 1fr 1fr auto' : '1.2fr 1fr 1fr 1fr' : onDelete ? '1.2fr 1fr 1fr auto' : '1.2fr 1fr 1fr',
          minWidth: isMobile ? 420 : undefined,
          background: T.surfaceSubtle,
          borderBottom: `1px solid ${T.border}`
        }} data-id="element-375" data-mp="21r7c5pcbtu40">
            
            {(mostrarCosto ? onDelete ? ['Fecha', 'Lectura', 'Consumo', 'Costo', ''] : ['Fecha', 'Lectura', 'Consumo', 'Costo'] : onDelete ? ['Fecha', 'Lectura', 'Consumo', ''] : ['Fecha', 'Lectura', 'Consumo']).map((h, i) => <div key={h} style={{
            padding: '12px 16px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: T.textMuted,
            textAlign: i === 0 ? 'left' : 'right'
          }} data-id="element-376" data-mp="2tk7vnfno7bv9" data-mpi={i}>
              
                {h}
              </div>)}
          </div>
          {series.length === 0 && <div style={{
          padding: '28px 16px',
          textAlign: 'center',
          color: T.textMuted,
          fontSize: 13
        }} data-id="element-377" data-mp="dtg5fl6ktli7">
            
              Sin registros aún
            </div>}
          {histItems.map((c, i, arr) => <div key={c.id} style={{
          display: 'grid',
          gridTemplateColumns: mostrarCosto ? onDelete ? '1.2fr 1fr 1fr 1fr auto' : '1.2fr 1fr 1fr 1fr' : onDelete ? '1.2fr 1fr 1fr auto' : '1.2fr 1fr 1fr',
          minWidth: isMobile ? 420 : undefined,
          borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
          alignItems: 'center'
        }} data-id="element-378" data-mp="15ni5twlmajz3" data-mpi={i}>
            
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.text,
            fontWeight: 600
          }} data-id="element-379" data-mp="1pbhff44g3l2x">
              
                {drDateTime(c.fecha)}
              </div>
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.textSecondary,
            textAlign: 'right'
          }} data-id="element-380" data-mp="28dqaz2rk6bun">
              
                {kwhFmt(c.kwh)}
              </div>
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.text,
            fontWeight: 600,
            textAlign: 'right'
          }} data-id="element-381" data-mp="1ymvobxaeha8o">
              
                {c.consumo > 0 ? kwhFmt(c.consumo) : '—'}
              </div>
              {mostrarCosto && <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.accent,
            fontWeight: 600,
            textAlign: 'right'
          }} data-id="element-382" data-mp="1bp27dbglar4r">
              
                  {c.consumo > 0 ? money(c.consumo * TARIFA) : '—'}
                </div>}
              {onDelete && <div style={{
            padding: '8px 16px',
            textAlign: 'right'
          }} data-id="element-383" data-mp="3a2tv1ui3vk6q">
              
                  <button onClick={() => setConfirm(c.id)} style={{
              ...ghostButton,
              padding: '4px 8px',
              fontSize: 11,
              color: c.archivado ? T.info : T.warn,
              borderColor: c.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
              background: c.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
            }} data-id="element-384" data-mp="ydnlzxkkwu9o">
                
                    {c.archivado ? 'Desarchivar' : 'Archivar'}
                  </button>
                </div>}
            </div>)}
          {series.length > 0 && <div style={{
          padding: '4px 16px 14px'
        }} data-id="element-385" data-mp="1r45ooxhe4tmk">
            
              <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="registros" data-id="element-386" data-mp="1m6w2dqpasy2y" />
            
            </div>}
        </div>
      </div>

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-387" data-mp="3nysep2jaibys">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-388" data-mp="330i4rynl0g16">
          
            ¿Estás seguro de que deseas{' '}
            {state.consumos.find(c => c.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            este registro de consumo?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-389" data-mp="2k2fap5g6fswi">
          
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-390" data-mp="2o38w9s65cvm8">
            
              Cancelar
            </button>
            <button onClick={() => {
          if (onDelete) onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: state.consumos.find(c => c.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: state.consumos.find(c => c.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-391" data-mp="2y1wzhkth365v">
            
              Sí,{' '}
              {state.consumos.find(c => c.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}
    </PageShell>;
}